import type { Episode } from "@/hooks/useEpisodes";
import { theme } from "@/lib/theme";

interface EpisodeCardProps {
  episode: Episode;
  onClick: () => void;
}

const truncateText = (text: string, maxLength: number) => {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trim()}...`;
};

export default function EpisodeCard({ episode, onClick }: EpisodeCardProps) {
  return (
    <div className="h-full">
      <div className="relative group cursor-pointer h-full" onClick={onClick}>
        <div className="absolute inset-0 bg-gray-900 dark:bg-gray-700 rounded-xl translate-y-2 translate-x-2 transition-transform group-hover:translate-y-3 group-hover:translate-x-3" />

        <div className={`relative z-10 ${theme.cards.episode} p-8 transition-transform group-hover:-translate-y-1 group-hover:-translate-x-1 h-full flex flex-col`}>
          <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">{episode.title}</h3>

          <p className="text-gray-700 dark:text-gray-300 mb-4">{truncateText(episode.description, 150)}</p>

          <div className="space-y-2 mb-4">
            <div className="flex flex-wrap gap-2 justify-center">
              {episode.formatTags[0] && (
                <span className={`px-3 py-1.5 ${theme.tags.format} rounded-full text-sm font-bold text-center min-w-[180px] md:min-w-[220px] inline-block`}>
                  {episode.formatTags[0]}
                </span>
              )}
            </div>

            <div className="flex flex-wrap gap-2 justify-center">
              {episode.themeTags.map(tag => (
                <span key={tag} className={`px-3 py-1.5 ${theme.tags.theme} rounded-full text-sm text-center min-w-[180px] md:min-w-[220px] inline-block`}>
                  {tag}
                </span>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 justify-center">
              {episode.topicTags.map(tag => (
                <span key={tag} className={`px-3 py-1.5 ${theme.tags.topic} rounded-full text-sm text-center min-w-[180px] md:min-w-[220px] inline-block`}>
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {episode.audioUrl ? (
            <a
              href={episode.audioUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-block px-6 py-2 ${theme.tags.format} rounded-lg font-bold hover:-translate-y-1 transition-transform`}
              onClick={event => event.stopPropagation()}
            >
              Listen
            </a>
          ) : (
            <span className="inline-block px-6 py-2 bg-gray-300 dark:bg-gray-600 border-3 border-gray-900 dark:border-gray-700 rounded-lg font-bold text-gray-600 dark:text-gray-300 cursor-not-allowed">
              No Audio Available
            </span>
          )}

          <div className="flex-grow" />
        </div>
      </div>
    </div>
  );
}
